import classNames from "classnames/bind";
import React from "react";
import styles from "../FilterBuilder.module.scss";
import {
  FilterBuilderFilterType,
  FilterBuilderProps,
  FilterBuilderRecursionDataType,
} from "../types";

import { LogicalOperators } from "@validereinc/utilities";
import { unset } from "es-toolkit/compat";
import { produce } from "immer";
import { Button } from "../../Button";
import { DropdownMenu } from "../../DropdownMenu";
import { getDeepReference, translateAndOr } from "../FilterBuilder.helpers";

const cx = classNames.bind(styles);

export const FilterBuilderSubGroupHeaderAndOptions = ({
  _recursion,
  setFilter,
  isDisabled,
  onFilterChange,
}: {
  _recursion: FilterBuilderRecursionDataType;
  setFilter: React.Dispatch<React.SetStateAction<FilterBuilderFilterType>>;
  isDisabled?: boolean;
  onFilterChange?: FilterBuilderProps["onFilterChange"];
}) => {
  return (
    <div className={cx("subgroup-title-container")}>
      <div className={cx("label")}>Sub-group</div>
      <div className={cx("subgroup-actions")}>
        {!isDisabled ? (
          <DropdownMenu
            options={[LogicalOperators.and, LogicalOperators.or].map(
              (option) => ({
                label: translateAndOr(option),
                onClick: () => {
                  const oldKey = _recursion.currentJoinOperator;
                  const newKey = option;

                  setFilter((oldFilter: FilterBuilderFilterType) => {
                    const producedFilter = produce(
                      oldFilter,
                      (draft: FilterBuilderFilterType) => {
                        const referenceToGroup =
                          getDeepReference(
                            draft,
                            _recursion.prefixKey.slice(0, -1)
                          ) ?? draft;

                        delete Object.assign(referenceToGroup, {
                          [newKey]: referenceToGroup[oldKey],
                        })[oldKey];
                      }
                    );

                    onFilterChange?.(producedFilter);
                    return producedFilter;
                  });
                },
              })
            )}
          >
            <Button
              variant="text"
              icon="caret-down"
              iconPosition="right"
              iconProps={{ weight: "fill", size: 14 }}
              size="small"
              className={cx("action-dropdown")}
            >
              {translateAndOr(_recursion.currentJoinOperator)}
            </Button>
          </DropdownMenu>
        ) : null}
        {!isDisabled ? (
          <Button
            className={cx("action-button")}
            onClick={() => {
              setFilter((oldFilter: FilterBuilderFilterType) => {
                const producedFilter = produce(
                  oldFilter,
                  (draft: FilterBuilderFilterType) => {
                    // Splice directly from $and or $or arrays, instead of just "delete"ing the path:
                    const previousKey = _recursion.prefixKey.slice(0, -1);
                    if (previousKey.length === 0) {
                      // We're at root:
                      unset(draft, _recursion.prefixKey.join("."));
                      return;
                    }
                    const indexToDelete = previousKey[
                      previousKey.length - 1
                    ] as number;

                    if (typeof indexToDelete !== "number") return;

                    const referenceToArray = getDeepReference(
                      draft,
                      _recursion.prefixKey.slice(0, -2)
                    );

                    referenceToArray.splice(indexToDelete, 1);
                  }
                );

                onFilterChange?.(producedFilter);
                return producedFilter;
              });
            }}
            icon="trash"
          />
        ) : null}
      </div>
    </div>
  );
};
